import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { Market, Selection } from '@/types/api';
import { eventPath, formatKickoff } from './marketUi';

export function Glyph({ icon, size = 18 }: { icon: ReactNode; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      {icon}
    </svg>
  );
}

export function MarketSkeleton() {
  return (
    <div className="rounded-2xl border border-white/[0.08] bg-brand-surface/80 p-4 animate-pulse">
      <div className="flex items-center justify-between mb-3">
        <div className="h-3 w-16 rounded bg-white/10" />
        <div className="h-3 w-10 rounded bg-white/10" />
      </div>
      <div className="h-4 w-3/4 rounded bg-white/10 mb-4" />
      <div className="flex gap-1.5">
        <div className="flex-1 h-12 rounded-[10px] bg-white/[0.05]" />
        <div className="flex-1 h-12 rounded-[10px] bg-white/[0.05]" />
        <div className="flex-1 h-12 rounded-[10px] bg-white/[0.05]" />
      </div>
    </div>
  );
}

export function EmptyState({ icon, title, subtitle, action }: { icon: ReactNode; title: string; subtitle: string; action?: ReactNode }) {
  return (
    <div className="rounded-2xl border border-dashed border-white/[0.12] bg-brand-surface/40 p-8 flex flex-col items-center text-center gap-2">
      <span className="w-11 h-11 rounded-2xl grid place-items-center bg-white/[0.04] text-white/35"><Glyph icon={icon} size={22} /></span>
      <div className="font-display font-bold text-[15px] text-white/80">{title}</div>
      <p className="text-sm text-white/45 max-w-xs">{subtitle}</p>
      {action}
    </div>
  );
}

export function SectionHeader({ title, meta, action }: { title: string; meta?: string; action?: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between mb-3">
      <h3 className="font-display font-bold text-[17px] tracking-[-0.01em]">{title}</h3>
      {action ?? (meta && <span className="text-xs text-white/40">{meta}</span>)}
    </div>
  );
}

/**
 * Home · (Draw) · Away, in that order, for a two-sided event. The feed lists
 * selections in provider order, which puts the draw first on some sports and
 * the away side first on others; a punter reads a match card left-to-right as
 * it is named. Anything that does not match the event label keeps its place.
 */
function orderTwoSided(market: Market): Selection[] {
  const [home, away] = market.eventLabel.split(/\s+vs\.?\s+/i).map((t) => t.trim().toLowerCase());
  if (!home || !away) return market.selections;
  const rank = (s: Selection) => {
    const l = s.label.trim().toLowerCase();
    if (l === home) return 0;
    if (l === away) return 2;
    if (l === 'draw' || l === 'tie') return 1;
    return 1;
  };
  return [...market.selections].sort((a, b) => rank(a) - rank(b));
}

/**
 * The odds buttons of ONE market. Two-way (team h2h) stays side-by-side, a
 * three-way (soccer with the draw) is a row of three. Everything else — racing
 * fields of 8-20 runners, player markets — is a VERTICAL list, one runner per
 * row: a wrapping row of equal tiles turns a field into two or three ragged
 * rows where runner order is no longer readable, and runner order is the thing
 * a punter scans.
 */
/* `active` is a predicate, not an id: since 0049 the slip holds several legs
   across several markets, and every held runner must light up, not just the
   one on the market the customer tapped last. */
export function SelectionButtons({ market, active, onPick }: { market: Market; active?: (selectionId: string) => boolean; onPick: (m: Market, s: Selection) => void }) {
  const n = market.selections.length;
  if (n === 2 || n === 3) {
    return (
      <div className={`grid gap-2 ${n === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
        {orderTwoSided(market).map((sel) => {
          const isActive = active?.(sel.id) === true;
          return (
            <button
              key={sel.id}
              onClick={() => onPick(market, sel)}
              className={`min-w-0 rounded-xl border px-2 py-2 text-center transition ${isActive ? 'border-brand-teal bg-brand-teal/10' : 'border-white/[0.08] bg-brand-navy2 hover:border-white/25 hover:bg-white/[0.04]'}`}
            >
              <div className="text-[10px] font-bold uppercase tracking-[0.06em] text-white/45 truncate">{sel.label}</div>
              <div className={`font-display text-[17px] font-extrabold tracking-[-0.01em] mt-0.5 ${isActive ? 'text-brand-teal' : 'text-white'}`}>{sel.decimalOdds.toFixed(2)}</div>
            </button>
          );
        })}
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-1.5">
      {market.selections.map((sel) => {
        const isActive = active?.(sel.id) === true;
        return (
          <button
            key={sel.id}
            onClick={() => onPick(market, sel)}
            className={`w-full flex items-center justify-between gap-3 rounded-xl border px-3 py-2 text-left transition ${isActive ? 'border-brand-teal bg-brand-teal/10' : 'border-white/[0.08] bg-brand-navy2 hover:border-white/25 hover:bg-white/[0.04]'}`}
          >
            <span className="text-[13px] font-medium text-white/80 truncate">{sel.label}</span>
            <span className={`font-display text-[16px] font-extrabold tabular-nums tracking-[-0.01em] shrink-0 ${isActive ? 'text-brand-teal' : 'text-white'}`}>{sel.decimalOdds.toFixed(2)}</span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * A board card: one event's PRIMARY market, with the event name linking into
 * the event page where every market on that fixture lives. `marketCount` is
 * the number of markets on the fixture so the link can say how many there are.
 */
export function MatchCard({ market, marketCount = 1, active, onPick }: { market: Market; marketCount?: number; active?: (selectionId: string) => boolean; onPick: (m: Market, s: Selection) => void }) {
  // eventLabel is "Home vs Away"; split for the stacked two-team layout.
  const teams = market.eventLabel.split(/\s+vs\.?\s+/i);
  const league = market.league || market.sport?.toUpperCase();
  const to = eventPath(market);
  return (
    <div className="rounded-2xl border border-white/[0.08] bg-brand-surface/80 p-4 hover:border-white/15 transition">
      {/* Time + league */}
      <div className="flex items-center justify-between mb-3">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-white/45">
          {market.live && <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />}
          {market.live ? 'LIVE' : formatKickoff(market.startAt)}
        </span>
        <span className="text-[10px] font-semibold uppercase tracking-[0.08em] rounded-md bg-brand-surface2 px-2 py-0.5 text-white/50">{league}</span>
      </div>

      {/* Teams — the event name is the way INTO the event. */}
      <Link to={to} className="block mb-3 leading-tight group">
        {teams.length === 2 ? (
          <>
            <div className="font-semibold text-[15px] truncate group-hover:text-brand-teal transition">{teams[0]}</div>
            <div className="font-semibold text-[15px] truncate text-white/85 group-hover:text-brand-teal transition">{teams[1]}</div>
          </>
        ) : (
          <div className="font-semibold text-[15px] group-hover:text-brand-teal transition">{market.eventLabel}</div>
        )}
      </Link>

      <SelectionButtons market={market} active={active} onPick={onPick} />

      <Link to={to} className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-brand-teal hover:brightness-110">
        {marketCount > 1 ? `All ${marketCount} markets` : 'All markets'} ›
      </Link>
    </div>
  );
}
